import { faClipboard } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Theme, Tooltip, useTheme } from '@mui/material';
import { useState } from 'react';

// Create a custom style class for the copy button
const copyButtonStyleNotHovered = {
    cursor: 'pointer',
    opacity: 0.7,
    transition: 'opacity 0.2s',
};

const copyButtonStyleHovered = {
    cursor: 'pointer',
    opacity: 1,
    transition: 'opacity 0.2s',
};

const copyButtonStyleClicked = {
    cursor: 'pointer',
    opacity: 0.5,
    transition: 'opacity 0.1s',
};

/**
 * CopyToClipboardButton component allows users to copy text to clipboard.
 * It displays a clipboard icon that changes style on hover.
 *
 * @param {Object} props - Component properties
 * @param {string} props.text - The text to be copied to clipboard
 * @param {string} [props.color] - Optional color for the icon
 * @returns {JSX.Element} The rendered component
 */
const CopyToClipboardButton = ({ text, color, disabled }: { text: string; color?: string; disabled?: boolean }): JSX.Element => {
    const theme: Theme = useTheme();
    const [hovered, setHovered] = useState(false);
    const [clicked, setClicked] = useState(false);

    const toggleHover = () => setHovered(!hovered);

    const handleCopy = () => {
        setClicked(true);
        setTimeout(() => setClicked(false), 200); // Reset clicked state after 200ms

        // Use the Clipboard API to copy text to clipboard
        if (!navigator.clipboard) {
            console.error('Clipboard API not supported');
            return;
        }

        if (!text) {
            console.error('No text provided to copy');
            return;
        }

        if (typeof text !== 'string') {
            console.error('Text to copy must be a string');
            return;
        }

        if (text.length === 0) {
            console.error('Text to copy cannot be empty');
            return;
        }

        if (!navigator.clipboard.writeText) {
            console.error('Clipboard writeText method not supported');
            return;
        }
        navigator.clipboard
            .writeText(text)
            .then(() => {
                console.log('Text copied to clipboard');
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    return (
        <Tooltip title="Copy ID to clipboard" arrow={true} placement="top">
            <span>
                <FontAwesomeIcon
                    icon={faClipboard}
                    color={disabled ? theme.palette.action.disabled : color || 'inherit'}
                    onClick={handleCopy}
                    onMouseEnter={toggleHover}
                    onMouseLeave={toggleHover}
                    style={clicked ? copyButtonStyleClicked : hovered ? copyButtonStyleHovered : copyButtonStyleNotHovered}
                />
            </span>
        </Tooltip>
    );
};

export default CopyToClipboardButton;
